import React, { useState } from 'react';
import { DOMAINS, formatRole } from '../permissionConfig';

/* ---------------------------------------------------------------------- */
/* Icons                                                                   */
/* ---------------------------------------------------------------------- */
const IconUser = (p) => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" {...p}>
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
  </svg>
);
const Chevron = ({ open, size = 16, color = "#9A90AE" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform .15s ease" }}>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);
const IconShield = (p) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6E58A6" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}>
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);
const IconLogout = (p) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#C25E77" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);
const Tick = ({ color }) => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
);
const Dash = ({ color, size = 10 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round"><line x1="4" y1="12" x2="20" y2="12" /></svg>
);
const Lock = ({ color }) => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

/* ---------------------------------------------------------------------- */
/* Real permission data — built from the actual DOMAINS manifest and the  */
/* logged-in user's real function_permissions (admin = everything, same   */
/* bypass logic as hasFunctionAccess()).                                  */
/* ---------------------------------------------------------------------- */
function buildPermissions(user) {
    return DOMAINS.map(domain => {
        const grantedKeys = user?.role === 'admin'
            ? domain.functions.map(f => f.key)
            : (user?.function_permissions?.[domain.key] || []);

        const fns = domain.functions.map(f => [f.label, grantedKeys.includes(f.key) ? 1 : 0]);
        const granted = fns.filter(f => f[1]).length;
        const total = fns.length;
        const isFull = granted === total;
        const isNone = granted === 0;

        let scope, pillBg, pillColor, badge;
        if (isNone) {
            scope = 'No access'; pillBg = '#F1EEF6'; pillColor = '#9A90AE'; badge = <Lock color="#B7AFC6" />;
        } else if (isFull) {
            scope = 'Full access'; pillBg = domain.tint; pillColor = domain.color; badge = <Tick color={domain.color} />;
        } else {
            scope = `${granted}/${total} functions`; pillBg = '#F5EDDC'; pillColor = '#9A7B3E'; badge = <Dash color={domain.color} />;
        }

        return { name: domain.label, tint: domain.tint, color: domain.color, fns, scope, pillBg, pillColor, badge, isFull, isNone, expandable: !isNone };
    });
}

/* ---------------------------------------------------------------------- */
/* Component                                                               */
/* ---------------------------------------------------------------------- */
export default function ProfileMenu({ user, onLogout }) {
    const [menuOpen, setMenuOpen] = useState(false);
    const [permsOpen, setPermsOpen] = useState(true);
    const [expandedPage, setExpandedPage] = useState(null);

    const name   = user?.display_name || user?.username || 'User';
    const role   = user?.role || '';
    const handle = user?.username || '';
    const permissions = buildPermissions(user);

    return (
        <div style={{ position: 'relative', fontFamily: "'Poppins', sans-serif" }}>
            {/* TRIGGER */}
            <div
                onClick={() => setMenuOpen(v => !v)}
                style={{ display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer', padding: '6px 10px', borderRadius: 14, background: menuOpen ? '#F6F2FB' : 'transparent' }}
            >
                <div style={{ width: 52, height: 52, borderRadius: 15, background: 'linear-gradient(135deg,#8E74C9,#5D4A8C)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', flex: 'none' }}>
                    <IconUser />
                </div>
                <div style={{ lineHeight: 1.2 }}>
                    <div style={{ fontWeight: 600, fontSize: 20, color: '#231C30' }}>{name}</div>
                    <div style={{ fontSize: 13, color: '#8C82A0', fontWeight: 400 }}>{formatRole(role)}</div>
                </div>
                <Chevron open={menuOpen} />
            </div>

            {/* Backdrop to close on outside click */}
            {menuOpen && (
                <div onClick={() => setMenuOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 29 }} />
            )}

            {/* DROPDOWN */}
            {menuOpen && (
                <div style={{ position: 'absolute', top: 66, right: 0, width: 290, background: '#fff', border: '1px solid #ECE7F2', borderRadius: 16, boxShadow: '0 16px 40px rgba(76,59,120,0.16)', overflow: 'hidden', zIndex: 30 }}>
                    <div style={{ padding: '16px 20px', borderBottom: '1px solid #F0ECF5' }}>
                        <div style={{ fontWeight: 600, fontSize: 15, color: '#231C30' }}>{name}</div>
                        <div style={{ fontSize: 12.5, color: '#8C82A0', fontWeight: 300 }}>@{handle} &middot; {formatRole(role)}</div>
                    </div>

                    <div style={{ padding: 8 }}>
                        {/* YOUR PERMISSIONS */}
                        <div
                            onClick={() => setPermsOpen(v => !v)}
                            style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 11, cursor: 'pointer', background: permsOpen ? '#F6F2FB' : 'transparent' }}
                        >
                            <IconShield />
                            <span style={{ flex: 1, fontSize: 14, color: '#231C30', fontWeight: 500 }}>Your Permissions</span>
                            <Chevron open={permsOpen} size={14} />
                        </div>

                        {permsOpen && (
                            <div style={{ padding: '4px 6px 10px 14px', display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {permissions.map(p => {
                                    const isExpanded = expandedPage === p.name;
                                    return (
                                        <div key={p.name}>
                                            <div
                                                onClick={() => p.expandable && setExpandedPage(cur => (cur === p.name ? null : p.name))}
                                                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px', borderRadius: 9, cursor: p.expandable ? 'pointer' : 'default', opacity: p.isNone ? 0.55 : 1 }}
                                            >
                                                <span style={{ width: 22, height: 22, borderRadius: 7, background: p.tint, display: 'flex', alignItems: 'center', justifyContent: 'center', color: p.color, flex: 'none' }}>
                                                    {p.badge}
                                                </span>
                                                <span style={{ fontSize: 13, color: '#4A4356', fontWeight: 500 }}>{p.name}</span>
                                                <span style={{ marginLeft: 'auto', fontSize: 10.5, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: p.pillBg, color: p.pillColor }}>{p.scope}</span>
                                                {p.expandable && <Chevron open={isExpanded} size={12} color="#B7AFC6" />}
                                            </div>

                                            {p.expandable && isExpanded && (
                                                <div style={{ margin: '2px 0 6px 32px', display: 'flex', flexDirection: 'column', gap: 5, paddingLeft: 10, borderLeft: '2px solid #F0ECF5' }}>
                                                    {p.fns.map(([fname, on]) => (
                                                        <div key={fname} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                            <span style={{ width: 15, height: 15, borderRadius: 5, background: on ? p.tint : '#F1EEF6', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
                                                                {on ? <Tick color={p.color} /> : <Dash color="#C7BFD6" size={9} />}
                                                            </span>
                                                            <span style={{ fontSize: 12, color: on ? '#4A4356' : '#B7AFC6', fontWeight: 400 }}>{fname}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                                <div style={{ marginTop: 4, padding: '8px 10px', fontSize: 11.5, color: '#A79EB6', fontWeight: 300, lineHeight: 1.5 }}>
                                    {role === 'admin'
                                        ? 'You have unrestricted access to every domain and function.'
                                        : 'Need access to something greyed out? Ask your admin to update your permissions.'}
                                </div>
                            </div>
                        )}

                        {/* LOG OUT */}
                        <div
                            onClick={onLogout}
                            style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 11, cursor: 'pointer' }}
                            onMouseEnter={e => (e.currentTarget.style.background = '#FBF1F3')}
                            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                        >
                            <IconLogout />
                            <span style={{ fontSize: 14, color: '#C25E77', fontWeight: 500 }}>Log Out</span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}